import React, { useState, useEffect } from "react";
import { ReactComponent as LiteBgImage } from "../../assets/icons/svg/triva_lite.svg";
import { ReactComponent as SparkBgImage } from "../../assets/icons/svg/triva_spark.svg";
import { ReactComponent as IgniteBgImage } from "../../assets/icons/svg/triva_ignite.svg";
import { ReactComponent as ThriveBgImage } from "../../assets/icons/svg/triva_thrive.svg";
import { ReactComponent as LiteBgImageIcon } from "../../assets/icons/svg/triva_lite-icon.svg";
import { ReactComponent as SparkBgImageIcon } from "../../assets/icons/svg/triva_spark-icon.svg";
import { ReactComponent as IgniteBgImageIcon } from "../../assets/icons/svg/triva_ignite-icon.svg";
import { ReactComponent as ThriveBgImageIcon } from "../../assets/icons/svg/triva_thrive-icon.svg";
import { ReactComponent as Star } from "../../assets/icons/svg/triva_premium-icon.svg";
import { ChevronLeft, ChevronRight, TrendingUp, AlertCircle } from "lucide-react";
import RecommendedPlanDetails from "./Recommended_Plan_Details";
import { PlanSelectionPayment } from "./Plan_Selection_Payment";
import PremiumSignaturePlanDetails from "./Premium_Signature_Plan_Details";
import PaymentSuccessPage from "./Payment_Success_Page_Mobile";
import InAppWebView from "./InAppWebView";
import GradientBorderPremiumTag from "../ui/Gradient_Border_Premium_Tag";
import LoginForm from "../desktop_screens/auth/Login_Form";
import OTPVerification from "../desktop_screens/auth/OTP_Verification";
import UserRegistration from "../desktop_screens/auth/User_Registration";
import { API_URLS } from "../api/urls";
import { AuthStorage } from "../desktop_screens/auth/AuthStorage";
import {
  SIGNATURE_PLAN_TITLE,
  SIGNATURE_PLAN_SUBTITLE,
  SIGNATURE_PLAN_KNOW_MORE_URL,
} from "../data/signaturePlanData";

interface TrivaPlansMobileProps {
  userId: number | null;
  authToken?: string | null;
  makeAuthenticatedApiCall?: (url: string, options?: RequestInit) => Promise<Response>;
  onPaymentSuccess?: (
    planType: "lite" | "spark" | "ignite" | "thrive" | "premium",
    duration: number,
    paymentDetails: {
      orderId: string;
      paymentId: string;
      amount: number;
      transactionDate: string;
      invoiceNumber?: string;
      invoicePdf?: string;
      userPaymentId?: number;
    }
  ) => void;
  deeplinkPlan?: string | null;
}

type StandardPlanType = "lite" | "spark" | "ignite" | "thrive";
const isStandardPlan = (planType: string | null): planType is StandardPlanType => {
  return planType === "lite" || planType === "spark" || planType === "ignite" || planType === "thrive";
};

type ViewType = 
  | "plans" 
  | "planDetails" 
  | "login" 
  | "otpVerification" 
  | "userRegistration"
  | "planSelectionPayment"
  | "premiumPlan"
  | "paymentSuccess"
  | "webview";

interface LoginData {
  Success: string;
  message: string;
  user_id: number;
  otp?: number;
  random_gen_no?: string;
  user_status: string;
  remaining_days: number;
  access_token: string;
  refresh_token: string;
  contactInfo: string;
  contactType: "phone" | "email";
  isNewUser?: boolean;
  isGoogleAuthenticated?: boolean;
}

interface UserData {
  user_id: number;
  user_mobileno: string;
  otp: string;
  user_fname: string;
  user_lname: string;
  goal: string;
  gender: string;
  age: number;
  city: string;
  state: string;
  dob: string;
  height_parameter: string;
  height_value: number;
  weight_parameter: string;
  weight_value: number;
  target_weight: number;
  target_weight_parameter: string;
  food_preference: string;
  progress: string;
  medical_conditions: string;
  last_status: string | null;
  profile_url: string;
}

interface ApiPlanData {
  subscription_plan_id: number;
  subscription_plan_name: string;
  std_subscription_perks: string;
  duration_and_price_and_perks: {
    standard_details_id: number;
    std_subscription_price: string;
    std_subscription_discount_price: string;
    duration_period: string;
    product_id: string;
    std_subscription_description: string;
    std_subscription_perfect_for: string;
    std_subscription_not_included: string;
  }[];
}

interface ApiResponse {
  success: string;
  message: string;
  plans: ApiPlanData[];
}

interface PlanData {
  id: string;
  title: string;
  description: string;
  price: string;
  originalPrice?: string;
  priceLabel: string;
  icon: React.ReactNode;
  BgImage: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  buttonBorderColor: string;
  cardBorderColor: string;
  pricingOptions: {
    duration: string;
    price: string;
    originalPrice?: string;
    standardDetailsId: number;
    productId: string;
  }[];
  whatYouGet: string[];
  whatsNotIncluded: string[];
}

interface PlanCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  price: string;
  originalPrice?: string;
  priceLabel?: string;
  BgImageComponent: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  textColor?: string;
  buttonBorderColor?: string;
  cardBorderColor?: string;
  onNavigateToPrice: () => void;
}

// PlanCard component to show discount pricing
const PlanCard: React.FC<PlanCardProps> = ({
  icon,
  title,
  description,
  price,
  originalPrice,
  priceLabel = "/month",
  BgImageComponent,
  textColor = "text-gray-700",
  buttonBorderColor = "border-gray-200",
  cardBorderColor = "border-gray-200",
  onNavigateToPrice,
}) => (
  <div
    className={`relative rounded-2xl p-4 mb-4 overflow-hidden border ${cardBorderColor}`}
    style={{ boxShadow: "0px 0px 6px 0px #0837370F" }}
  >
    <div className="absolute inset-0 w-full h-full">
      <BgImageComponent className="w-full h-full object-cover" />
    </div>

    <div className="relative z-10">
      <div className="flex items-start mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0`}>
          {icon}
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <h3 className={`text-[18px] font-semibold ${textColor}`}>{title}</h3>
          <p className={`text-[10px] ${textColor === "text-[#646464]"}`}>{description}</p>
        </div>
      </div>

      <div className="mb-3">
        <span className={`text-[14px] font-inter font-normal ${textColor === "text-[#202020]"}`}>
          Starting from
        </span>
      </div>

      <button
        onClick={onNavigateToPrice}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-white border ${buttonBorderColor} transition-colors duration-200`}
      >
        <div className="flex items-center gap-2">
          {/* Show strikethrough original price if discount exists */}
          {originalPrice && originalPrice !== price && (
            <div className="flex items-center">
              <span className="text-[14px] font-inter text-[#646464] line-through">
                ₹{originalPrice}
              </span>
            </div>
          )}
          
          {/* Current/Discounted Price */}
          <div className="flex items-center">
            <span className={`text-xl font-inter mr-1 ${textColor}`}>₹</span>
            <span className={`text-xl font-bold font-inter mr-1 ${textColor}`}>{price}</span>
            <span className={`text-[12px] font-medium font-inter ${textColor}`}>{priceLabel}</span>
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 ${textColor}`} />
      </button>
    </div>
  </div>
);

const TrivaPlansMobile: React.FC<TrivaPlansMobileProps> = ({
  userId: propUserId,
  authToken,
  makeAuthenticatedApiCall,
  onPaymentSuccess: propOnPaymentSuccess,
  deeplinkPlan,
}) => {
  // View state — if opened via deeplink jump straight to that plan's view.
  // Also read sessionStorage directly because AppContent's useEffect sets deeplinkPlan
  // AFTER this component already mounts, so the prop is null on first render.
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    const validPlans: StandardPlanType[] = ["lite", "spark", "ignite", "thrive"];
    const effectivePlan = deeplinkPlan || sessionStorage.getItem('triva_deeplink_plan');
    if (!effectivePlan) return "plans";
    if (effectivePlan === "signature") return "premiumPlan";
    return validPlans.includes(effectivePlan as StandardPlanType) ? "planDetails" : "plans";
  });
  const [selectedPlanType, setSelectedPlanType] = useState<"lite" | "spark" | "ignite" | "thrive" | "premium" | null>(() => {
    const validPlans: StandardPlanType[] = ["lite", "spark", "ignite", "thrive"];
    const effectivePlan = deeplinkPlan || sessionStorage.getItem('triva_deeplink_plan');
    if (!effectivePlan) return null;
    if (effectivePlan === "signature") return "premium";
    return validPlans.includes(effectivePlan as StandardPlanType) ? (effectivePlan as StandardPlanType) : null;
  });
  const [selectedPlanData, setSelectedPlanData] = useState<PlanData | null>(null);
  
  // Auth state
  const [authenticatedUserId, setAuthenticatedUserId] = useState<number | null>(null);
  const [currentLoginData, setCurrentLoginData] = useState<LoginData | null>(null);
  const [currentUserData, setCurrentUserData] = useState<UserData | null>(null);
  const [googleUserName, setGoogleUserName] = useState<string | null>(null);
  const [googleUserEmail, setGoogleUserEmail] = useState<string | null>(null);
  
  // WebView state
  const [showWebView, setShowWebView] = useState(false);
  const [webViewUrl, setWebViewUrl] = useState("");
  const [webViewTitle, setWebViewTitle] = useState("");
  
  // Plans data state
  const [allPlansData, setAllPlansData] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  
  // Payment success data
  const [paymentSuccessData, setPaymentSuccessData] = useState<{
  planType: "lite" | "spark" | "ignite" | "thrive" | "premium";
  duration: number;
  paymentDetails: {
    orderId: string;
    paymentId: string;
    amount: number;
    transactionDate: string;
    invoiceNumber?: string;
    invoicePdf?: string;
    userPaymentId?: number;
  };
} | null>(null);

  useEffect(() => {
    const initializeAuth = () => {
      if (AuthStorage.isAuthenticated()) {
        const userId = AuthStorage.getUserId();
        const userData = AuthStorage.getUserData();
        setAuthenticatedUserId(userId);
        setCurrentUserData(userData);
      }
    };
    initializeAuth();
  }, []);

  const effectiveUserId = authenticatedUserId || propUserId;

  useEffect(() => {
    fetchAndPrepareAllPlans();
  }, []);

  useEffect(() => {
    if (currentView !== "plans" && currentView !== "webview") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentView]);

  // Handle deeplink: navigate directly to plan details (auth already set by DeeplinkHandler)
  useEffect(() => {
    if (!deeplinkPlan) return;

    if (deeplinkPlan === "signature") {
      setCurrentView("premiumPlan");
      return;
    }

    if (allPlansData.length === 0) return;
    const validPlans: StandardPlanType[] = ["lite", "spark", "ignite", "thrive"];
    if (!validPlans.includes(deeplinkPlan as StandardPlanType)) return;

    const planData = allPlansData.find((p) => p.id === deeplinkPlan);
    if (!planData) return;

    setSelectedPlanType(deeplinkPlan as StandardPlanType);
    setSelectedPlanData(planData);
    setCurrentView("planDetails");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deeplinkPlan, allPlansData]);

  const fetchAndPrepareAllPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_URLS.Get_STANDARD_PLANS);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data: ApiResponse = await response.json();
      if (data.success !== "1") throw new Error(data.message || "Failed to fetch plans");

      const preparedPlans = createAllFourPlans(data.plans);
      setAllPlansData(preparedPlans);
    } catch (err) {
      console.error("Error fetching plans data:", err);
      setError(err instanceof Error ? err.message : "An unknown error occurred");
      const fallbackPlans = createFallbackPlans();
      setAllPlansData(fallbackPlans);
    } finally {
      setLoading(false);
    }
  };

  const getPlanConfig = (planId: string) => {
    const configs = {
      lite: {
        icon: <LiteBgImageIcon className="w-[36px] h-[36px]" />,
        BgImage: LiteBgImage,
        buttonBorderColor: "border-[#F0B0E0] border-[0.5px]",
        cardBorderColor: "border-[#E47DCB] border-[0.5px]",
      },
      spark: {
        icon: <SparkBgImageIcon className="w-[36px] h-[36px]" />,
        BgImage: SparkBgImage,
        buttonBorderColor: "border-[#E4CD6F] border-[0.5px]",
        cardBorderColor: "border-[#C5A344] border-[0.5px]",
      },
      ignite: {
        icon: <IgniteBgImageIcon className="w-[36px] h-[36px]" />,
        BgImage: IgniteBgImage,
        buttonBorderColor: "border-[#BCDD73] border-[0.5px]",
        cardBorderColor: "border-[#86BC0B] border-[0.5px]",
      },
      thrive: {
        icon: <ThriveBgImageIcon className="w-[36px] h-[36px]" />,
        BgImage: ThriveBgImage,
        buttonBorderColor: "border-[#A78FFF] border-[0.5px]",
        cardBorderColor: "border-[#B7AAF5] border-[0.5px]",
      },
    };
    return configs[planId as keyof typeof configs];
  };

  const createAllFourPlans = (apiPlans: ApiPlanData[]): PlanData[] => {
    const allPlans: PlanData[] = [];
    const planTypes = ["lite", "spark", "ignite", "thrive"];

    planTypes.forEach((planId) => {
      const config = getPlanConfig(planId);
      const matchingApiPlan = findApiPlanByType(apiPlans, planId);

      if (matchingApiPlan) {
        try {
          const transformedPlan = transformApiDataToPlanData(matchingApiPlan, planId, config);
          allPlans.push(transformedPlan);
        } catch (error) {
          console.error(`Error transforming API plan for ${planId}:`, error);
          const fallbackPlan = createDefaultPlan(planId, config);
          allPlans.push(fallbackPlan);
        }
      } else {
        const defaultPlan = createDefaultPlan(planId, config);
        allPlans.push(defaultPlan);
      }
    });

    return allPlans;
  };

  const findApiPlanByType = (apiPlans: ApiPlanData[], planType: string): ApiPlanData | null => {
    const planMappings = {
      lite: "Triva Lite",
      spark: "Triva Spark",
      ignite: "Triva Ignite",
      thrive: "Triva Thrive",
    };

    const searchTerm = planMappings[planType as keyof typeof planMappings];
    if (!searchTerm) return null;

    return apiPlans.find((plan) =>
      plan.subscription_plan_name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || null;
  };

  const createDefaultPlan = (planId: string, config: ReturnType<typeof getPlanConfig>): PlanData => {
    const defaultTitles: Record<string, string> = {
      lite: "Triva Lite",
      spark: "Triva Spark",
      ignite: "Triva Ignite",
      thrive: "Triva Thrive",
    };

    const defaultDescriptions: Record<string, string> = {
      lite: "App only, Self-starter plan, no nutritionist support",
      spark: "One-time nutritionist connect and lifestyle guidance",
      ignite: "Triva App + Nutritionist Bi-Monthly Check-In",
      thrive: "Triva App + Nutritionist and Weekly Check-In",
    };

    return {
      id: planId,
      title: defaultTitles[planId] || "Triva Plan",
      description: defaultDescriptions[planId] || "Nutrition and wellness plan",
      price: "99",
      priceLabel: "/month",
      icon: config.icon,
      BgImage: config.BgImage,
      buttonBorderColor: config.buttonBorderColor,
      cardBorderColor: config.cardBorderColor,
      pricingOptions: [],
      whatYouGet: [],
      whatsNotIncluded: [],
    };
  };

  const createFallbackPlans = (): PlanData[] => {
    const planTypes = ["lite", "spark", "ignite", "thrive"];
    return planTypes.map((planId) => {
      const config = getPlanConfig(planId);
      return createDefaultPlan(planId, config);
    });
  };

  // ✅ CHANGED: Transform API data to use discount pricing
  const transformApiDataToPlanData = (
    apiPlan: ApiPlanData,
    planId: string,
    config: ReturnType<typeof getPlanConfig>
  ): PlanData => {
    const planNameMatch = apiPlan.subscription_plan_name.match(/^([^(]+)(?:\s*\(([^)]+)\))?/);
    const title = planNameMatch ? planNameMatch[1].trim() : apiPlan.subscription_plan_name;
    const description = planNameMatch && planNameMatch[2] ? planNameMatch[2].trim() : "";

    const firstOption = apiPlan.duration_and_price_and_perks?.[0];

    // ✅ CHANGED: Get the lowest discount price instead of actual price
    const prices = (apiPlan.duration_and_price_and_perks || [])
      .map(option => parseFloat(option.std_subscription_discount_price || "0"))
      .filter(price => price > 0);
    
    const lowestPrice = prices.length > 0 ? Math.min(...prices) : 99;
    
    // ✅ CHANGED: Get the corresponding original (non-discounted) price for the lowest price option
    let lowestOriginalPrice: string | undefined = undefined;
    if (prices.length > 0) {
      const lowestPriceOption = apiPlan.duration_and_price_and_perks.find(
        option => parseFloat(option.std_subscription_discount_price) === lowestPrice
      );
      
      if (lowestPriceOption) {
        const originalPrice = parseFloat(lowestPriceOption.std_subscription_price);
        const actualPrice = parseFloat(lowestPriceOption.std_subscription_discount_price);
        
        // Only set originalPrice if it's different (i.e., there's a discount)
        if (originalPrice !== actualPrice) {
          lowestOriginalPrice = lowestPriceOption.std_subscription_price;
        }
      }
    }

    let priceLabel = "/month";
    if (firstOption?.duration_period === "1 time" || firstOption?.duration_period === "15 days") {
      priceLabel = "";
    }

    const formatDuration = (duration: string): string => {
      const mapping = {
        "1 Month": "For 1 month @",
        "3 Months": "For 3 month @", 
        "6 Months": "For 6 month @",
        "1 time": "For 1 time @",
        "15 days": "For 15 days @"
      };
      return mapping[duration as keyof typeof mapping] || `For ${duration} @`;
    };

    // ✅ CHANGED: Use discount price for calculations, original price for strikethrough
    const pricingOptions = apiPlan.duration_and_price_and_perks
      .sort((a, b) => {
        const durationOrder = { "1 Month": 1, "3 Months": 2, "6 Months": 3, "15 days": 0 };
        return (durationOrder[a.duration_period as keyof typeof durationOrder] || 999) - 
               (durationOrder[b.duration_period as keyof typeof durationOrder] || 999);
      })
      .map(option => ({
        duration: formatDuration(option.duration_period),
        price: option.std_subscription_discount_price, // ✅ Use discounted price for calculations
        originalPrice: option.std_subscription_discount_price !== option.std_subscription_price ? 
                      option.std_subscription_price : undefined, // ✅ Original price is the non-discounted one
        productId: option.product_id,
        standardDetailsId: option.standard_details_id
      }));

    const parsePerksToArray = (perks: string): string[] => {
      if (!perks) return [];
      let cleanedPerks = perks
        .replace(/<[^>]*>/g, '')
        .replace(/\\r\\n|\\r|\\n|\r\n|\r|\n/g, '|BREAK|')
        .trim();
      
      if (!cleanedPerks.includes('|BREAK|')) {
        return cleanedPerks ? [cleanedPerks] : [];
      }
      
      return cleanedPerks
        .split('|BREAK|')
        .map(perk => perk.trim())
        .filter(perk => perk && perk.length > 0)
        .map(perk => perk.replace(/,$/, ''));
    };

    return {
      id: planId,
      title,
      description,
      price: lowestPrice.toString(),
      originalPrice: lowestOriginalPrice,
      priceLabel,
      icon: config.icon,
      BgImage: config.BgImage,
      buttonBorderColor: config.buttonBorderColor,
      cardBorderColor: config.cardBorderColor,
      pricingOptions,
      whatYouGet: parsePerksToArray(apiPlan.std_subscription_perks),
      whatsNotIncluded: firstOption?.std_subscription_not_included ? 
        [firstOption.std_subscription_not_included] : [],
    };
  };

  // Navigation handlers
  const handleNavigateToPrice = (planName: string) => {
    const planTypeMap: Record<string, "lite" | "spark" | "ignite" | "thrive"> = {
      "Triva Lite": "lite",
      "Triva Spark": "spark",
      "Triva Ignite": "ignite",
      "Triva Thrive": "thrive",
    };

    const planType = planTypeMap[planName];
    if (planType) {
      const planData = allPlansData.find(p => p.id === planType);
      setSelectedPlanType(planType);
      setSelectedPlanData(planData || null);
      setCurrentView("planDetails");
    }
  };

  const handleBackToPlans = () => {
    setSelectedPlanType(null);
    setSelectedPlanData(null);
    setSelectedDuration(null);
    setCurrentView("plans");
    window.scrollTo(0, 0);
  };

  const handleSelectPlan = (planData: any, duration: string) => {
    setSelectedDuration(duration);

    if (authToken || AuthStorage.isAuthenticated()) {
      setCurrentView("planSelectionPayment");
      return;
    }

    setCurrentView("login");
  };

  const handleLoginSuccess = (userData?: any) => {
    console.log("Traditional login successful, awaiting OTP");
    // Note: Will proceed to OTP verification, then check selectedPlanType
  };

  // ✅ FIXED: Check for premium plan selection after successful OTP
  const handleOTPRequired = (loginData: LoginData) => {
    console.log("=== OTP Required ===");
    console.log("Login data:", loginData);
    console.log("Selected plan type:", selectedPlanType);
    setCurrentLoginData(loginData);
    
    AuthStorage.setAuthData(
      loginData.access_token,
      loginData.refresh_token,
      loginData.user_id
    );
    
    if (loginData.isGoogleAuthenticated) {
      setAuthenticatedUserId(loginData.user_id);
      
      // ✅ ADDED: Check if premium plan was selected before login
      if (selectedPlanType === "premium") {
        console.log("✅ Premium plan selected - redirecting to premium plan details");
        setCurrentView("premiumPlan");
      } else {
        setCurrentView("planSelectionPayment");
      }
    } else {
      setCurrentView("otpVerification");
    }
  };

  const handleBackFromLogin = () => {
    console.log("=== Back from Login ===");
    if (selectedPlanType && selectedPlanData) {
      setCurrentView("planDetails");
    } else if (selectedPlanType === "premium") {
      // ✅ ADDED: Go back to plans view if premium was selected
      setCurrentView("plans");
    } else {
      setCurrentView("plans");
    }
  };

  // ✅ FIXED: Check for premium plan selection after OTP verification
  const handleOTPVerificationSuccess = (userData: UserData) => {
    console.log("=== OTP Verification Success ===");
    console.log("User data:", userData);
    console.log("Selected plan type:", selectedPlanType);
    
    setCurrentUserData(userData);
    setAuthenticatedUserId(userData.user_id);

    // Re-set auth data with the verified user_id so AuthStorage is valid for
    // subsequent API calls (e.g. update_user in UserRegistration). The initial
    // USER_LOGIN token is reused; user_id is confirmed from the OTP response.
    if (currentLoginData) {
      AuthStorage.setAuthData(
        currentLoginData.access_token,
        currentLoginData.refresh_token,
        userData.user_id,
        userData
      );
    } else {
      AuthStorage.setUserData(userData);
    }

    if (!userData.user_fname || userData.user_fname.trim() === "") {
      setCurrentView("userRegistration");
    } else {
      // ✅ ADDED: Check if premium plan was selected before login
      if (selectedPlanType === "premium") {
        console.log("✅ Premium plan selected - redirecting to premium plan details");
        setCurrentView("premiumPlan");
      } else {
        setCurrentView("planSelectionPayment");
      }
    }
  };

  const handleBackFromOTP = () => {
    setCurrentView("login");
  };

  // ✅ FIXED: Check for premium plan selection after user registration
  const handleUserRegistrationSuccess = (firstName: string, lastName: string) => {
    console.log("=== User Registration Success ===");
    console.log("Name:", { firstName, lastName });
    console.log("Selected plan type:", selectedPlanType);
    
    if (currentUserData) {
      const updatedUserData = {
        ...currentUserData,
        user_fname: firstName,
        user_lname: lastName
      };
      setCurrentUserData(updatedUserData);
      
      AuthStorage.setUserData(updatedUserData);
    }
    
    // ✅ ADDED: Check if premium plan was selected before registration
    if (selectedPlanType === "premium") {
      console.log("✅ Premium plan selected - redirecting to premium plan details");
      setCurrentView("premiumPlan");
    } else {
      setCurrentView("planSelectionPayment");
    }
  };

  const handleBackFromRegistration = () => {
    if (currentLoginData) {
      setCurrentView("otpVerification");
    } else {
      setCurrentView("login");
    }
  };

  const handleBackFromPaymentSelection = () => {
    console.log("=== Back from Payment Selection ===");
    setCurrentView("planDetails");
  };

  const handlePaymentSuccess = (
    planType: "lite" | "spark" | "ignite" | "thrive" | "premium",
    duration: number,
    paymentDetails: {
      orderId: string;
      paymentId: string;
      amount: number;
      transactionDate: string;
      invoiceNumber?: string;
      userPaymentId?: number;
    }
  ) => {
    setPaymentSuccessData({ planType, duration, paymentDetails });
    setCurrentView("paymentSuccess");
    
    if (propOnPaymentSuccess) {
      propOnPaymentSuccess(planType, duration, paymentDetails);
    }
  };

  const handleNavigateHomeFromSuccess = () => {
    setPaymentSuccessData(null);
    setSelectedPlanType(null);
    setSelectedPlanData(null);
    setSelectedDuration(null);
    setCurrentView("plans");
    window.scrollTo(0, 0);
  };

  const handleCustomizedProgram = () => {
    console.log("=== Customized Program Selected ===");
    console.log("AuthStorage authenticated:", AuthStorage.isAuthenticated());
    
    setSelectedPlanType("premium");
    
    if (AuthStorage.isAuthenticated()) {
      console.log("✅ User authenticated - showing premium plan directly");
      setCurrentView("premiumPlan");
    } else {
      console.log("❌ User not authenticated - showing login");
      setCurrentView("login");
    }
  };

  const handleKnowMore = () => {
    setWebViewUrl(SIGNATURE_PLAN_KNOW_MORE_URL);
    setWebViewTitle("About Deepika Chalasani");
    setShowWebView(true);
  };

  const handleContactUs = () => {
    setWebViewUrl("https://mytriva.com/contact-us/");
    setWebViewTitle("Contact Us");
    setShowWebView(true);
  };

  const handleCloseWebView = () => {
    setShowWebView(false);
  };

  // Render current view
  if (showWebView) {
    return (
      <InAppWebView
        url={webViewUrl}
        title={webViewTitle}
        onClose={handleCloseWebView}
      />
    );
  }

  if (currentView === "premiumPlan") {
    return (
      <PremiumSignaturePlanDetails 
        userId={effectiveUserId} 
        authToken={authToken || null}  
        makeAuthenticatedApiCall={makeAuthenticatedApiCall}
        onBack={handleBackToPlans} 
      />
    );
  }

  if (currentView === "planDetails" && selectedPlanType && isStandardPlan(selectedPlanType)) {
    return (
      <RecommendedPlanDetails
        planType={selectedPlanType}
        userId={effectiveUserId}
        authToken={authToken || null}  
        onBack={handleBackToPlans}
        onSelectPlan={handleSelectPlan}
      />
    );
  }

  if (currentView === "login") {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        <LoginForm
          onBack={handleBackFromLogin}
          onLoginSuccess={handleLoginSuccess}
          onOTPRequired={handleOTPRequired}
          onGoogleLoginSuccess={(userData, needsRegistration) => {
            console.log("=== Google Login Success ===");
            console.log("Selected plan type:", selectedPlanType);

            // AuthStorage already has correct auth data set by Login_Form
            // userData here is googleUserInfo (Firebase), not the backend response
            const userId = AuthStorage.getUserId();
            if (userId) {
              setAuthenticatedUserId(userId);
            }

            // Store Google name/email as fallback for the payment screen
            if (userData?.firstName) {
              setGoogleUserName(
                `${userData.firstName} ${userData.lastName || ""}`.trim()
              );
            }
            if (userData?.email) {
              setGoogleUserEmail(userData.email);
            }

            if (needsRegistration) {
              setCurrentView("userRegistration");
            } else {
              if (selectedPlanType === "premium") {
                console.log("✅ Premium plan selected - redirecting to premium plan details");
                setCurrentView("premiumPlan");
              } else {
                setCurrentView("planSelectionPayment");
              }
            }
          }}
          isGoogleProcessing={false}
          onGoogleProcessingStart={() => {}}
        />
      </div>
    );
  }

  if (currentView === "otpVerification" && currentLoginData) {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        <OTPVerification
          contactInfo={currentLoginData.contactInfo}
          contactType={currentLoginData.contactType}
          userId={currentLoginData.user_id}
          expectedOTP={currentLoginData.otp}
          onBack={handleBackFromOTP}
          onVerificationSuccess={handleOTPVerificationSuccess}
        />
      </div>
    );
  }

  if (currentView === "userRegistration") {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        <UserRegistration
          onSubmit={handleUserRegistrationSuccess}
          onBack={handleBackFromRegistration}
          initialFirstName={currentUserData?.user_fname || ""}
          initialLastName={currentUserData?.user_lname || ""}
          userId={currentUserData?.user_id ?? AuthStorage.getUserId()}
          accessToken={currentLoginData?.access_token || AuthStorage.getAccessToken()}
        />
      </div>
    );
  }

 if (currentView === "planSelectionPayment" && selectedPlanType && selectedPlanData && isStandardPlan(selectedPlanType) && selectedDuration) {
  return (
    <PlanSelectionPayment
      planType={selectedPlanType}
      userId={effectiveUserId}
      authToken={null}
      planData={{
        title: selectedPlanData.title,
        pricingOptions: selectedPlanData.pricingOptions,
        whatYouGet: selectedPlanData.whatYouGet,
        whatsNotIncluded: selectedPlanData.whatsNotIncluded,
      }}
      selectedDuration={selectedDuration}
      onBack={handleBackFromPaymentSelection}
      onPaymentSuccess={handlePaymentSuccess}
      preFilledEmail={googleUserEmail || undefined}
      preFilledName={googleUserName || undefined}
    />
  );
}

  if (currentView === "paymentSuccess" && paymentSuccessData) {
    return (
      <PaymentSuccessPage
        userId={effectiveUserId}
        planType={paymentSuccessData.planType}
        selectedDuration={paymentSuccessData.duration}
        paymentDetails={paymentSuccessData.paymentDetails}
        onNavigateHome={handleNavigateHomeFromSuccess}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        <div className="flex items-center py-4 bg-white">
          <h1 className="text-[18px] font-semibold text-[#202020] font-inter">Triva plans</h1>
        </div>
        <div className="space-y-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-48"></div>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-2xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error && allPlansData.length === 0) {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        <div className="flex items-center py-4 bg-white">
          <h1 className="text-[18px] font-semibold text-[#202020] font-inter">Triva plans</h1>
        </div>
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-[#202020] mb-2">Unable to Load Plans</h2>
          <p className="text-[#646464] mb-4 text-sm">{error}</p>
          <button
            onClick={fetchAndPrepareAllPlans}
            className="bg-[#0DB5B5] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#0A9999] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Main plans list view
  return (
    <div className="min-h-screen bg-white px-[24px] mt-[32px] mb-3">
      {/* Header */}
      <div className="flex items-center py-4 bg-white">
        <h1 className="text-[18px] font-semibold text-[#202020] font-inter">Triva plans</h1>
      </div>

      <div className="">
        {/* Recommended Plan Label */}
        <h2 className="text-[16px] font-medium font-inter text-[#000000] mb-4">Recommended plan</h2>

        {/* Plan Cards with discount pricing */}
        {allPlansData.map((plan) => {
          return (
            <PlanCard
              key={plan.id}
              icon={plan.icon}
              title={plan.title}
              description={plan.description}
              price={plan.price}
              originalPrice={plan.originalPrice}
              priceLabel={plan.priceLabel}
              BgImageComponent={plan.BgImage}
              buttonBorderColor={plan.buttonBorderColor}
              cardBorderColor={plan.cardBorderColor}
              onNavigateToPrice={() => handleNavigateToPrice(plan.title)}
            />
          );
        })}

        {error && allPlansData.length > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-yellow-800 font-medium">Limited information available</p>
                <p className="text-yellow-700">Some plan details may not be current.</p>
                <button
                  onClick={fetchAndPrepareAllPlans}
                  className="mt-2 text-yellow-800 underline text-sm"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Premium Signature Plan */}
        <div
          className="border-[0.5px] border-[#0DB5B5] rounded-[12px] mb-4 bg-[#0DB5B5] mt-8"
          style={{ boxShadow: "0px 0px 6px 0px #0837370F" }}
        >
          <div className="rounded-[12px] p-5 bg-[#FAFFFF]">
            <div className="flex items-center mb-3">
              <GradientBorderPremiumTag>
                <Star className="w-4 h-4 mr-1 text-[#003E3E]" />
                Premium
              </GradientBorderPremiumTag>
            </div>
            <h2 className="text-[18px] font-inter font-semibold text-[#202020] mb-[2px]">
              {SIGNATURE_PLAN_TITLE}
            </h2>
            <p className="text-[10px] text-[#646464] font-inter font-normal mb-4">
              {SIGNATURE_PLAN_SUBTITLE}
            </p>

            <button
              onClick={handleCustomizedProgram}
              className="w-full flex items-center justify-between py-3 px-4 bg-white rounded-xl border-[0.5px] border-[#0DB5B5] transition-colors duration-200"
            >
              <span className="text-[#202020] font-inter font-medium text-[12px]">
                Customized program
              </span>
              <ChevronRight className="w-5 h-5 text-[#173430]" />
            </button>
          </div>

          <button
            onClick={handleKnowMore}
            className="w-full text-white py-3 px-4 font-medium font-inter flex items-center justify-center text-[12px]"
          >
            Know more about Deepika Chalasani
            <TrendingUp className="w-4 h-4 ml-2" />
          </button>
        </div>

        {/* Contact Us Section */}
        <div className="mt-4 text-center pb-4">
          <p className="text-[12px] font-medium font-inter text-[#646464]">
            Still have questions? We're here to help!{" "}
            <button
              onClick={handleContactUs}
              className="text-[#007E7F] font-medium underline"
            >
              Contact us
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrivaPlansMobile;